// src/components/common/InactivityWarningModal.tsx
import React, { useEffect, useRef } from 'react';
import '../../styles/InactivityWarningModal.css';

interface InactivityWarningModalProps {
  open: boolean;
  /** Milliseconds remaining before auto-logout. */
  remainingMs: number;
  onStaySignedIn: () => void;
}

function formatCountdown(ms: number): string {
  const totalSecs = Math.max(0, Math.ceil(ms / 1_000));
  const mins = Math.floor(totalSecs / 60);
  const secs = totalSecs % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

/** CSS selector for all naturally focusable elements inside the dialog. */
const FOCUSABLE =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

const InactivityWarningModal: React.FC<InactivityWarningModalProps> = ({
  open,
  remainingMs,
  onStaySignedIn,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  // Remember what had focus before the modal opened so we can restore it on close.
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;

    // Save caller's focus target
    previousFocusRef.current = document.activeElement as HTMLElement;

    // Move focus into the dialog (the button has autoFocus, but we also set it
    // here for safety in case autoFocus is suppressed by the browser/test env)
    const firstFocusable = dialogRef.current?.querySelector<HTMLElement>(FOCUSABLE);
    firstFocusable?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!dialogRef.current) return;

      // Escape = "Stay signed in" (dismiss, same as clicking the button)
      if (e.key === 'Escape') {
        e.preventDefault();
        onStaySignedIn();
        return;
      }

      // Trap Tab and Shift+Tab within the dialog
      if (e.key === 'Tab') {
        const focusable = Array.from(
          dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE),
        ).filter((el) => !el.hasAttribute('disabled'));

        if (focusable.length === 0) {
          e.preventDefault();
          return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
          // Shift+Tab from first → wrap to last
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          // Tab from last → wrap to first
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Restore focus to whatever had it before the modal opened
      previousFocusRef.current?.focus();
    };
  }, [open, onStaySignedIn]);

  if (!open) return null;

  return (
    <div
      ref={dialogRef}
      className="inactivity-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="inactivity-modal-title"
    >
      <div className="inactivity-modal">
        <h2 id="inactivity-modal-title" className="inactivity-modal__title">
          Are you still there?
        </h2>
        <p className="inactivity-modal__body">
          You'll be signed out in{' '}
          <span
            className="inactivity-modal__countdown"
            aria-live="polite"
            data-testid="inactivity-countdown"
          >
            {formatCountdown(remainingMs)}
          </span>
        </p>
        <button
          className="tesla-button tesla-button--primary inactivity-modal__cta"
          onClick={onStaySignedIn}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          data-testid="stay-signed-in-btn"
        >
          Stay signed in
        </button>
      </div>
    </div>
  );
};

export default InactivityWarningModal;
